import { createTerrariumScene as predecessor } from './scene-depth-water-ab.mjs';
import { adaptWatercourseSlope } from './watercourse-slope-adapter.mjs';

export function createTerrariumScene(T, { assets, data }) {
  const cfg = data?.watercourse;
  if (!cfg || cfg.schemaVersion !== 1 || cfg.placements?.length !== 8
      || !(cfg.sourceLongitudinalMetres > cfg.localLongitudinalMetres)
      || !(cfg.stoneBurialMetres > 0) || !(cfg.minimumWaterCoverMetres > 0)) {
    throw Error('Missing verified seated-watercourse configuration');
  }
  const w = predecessor(T, { assets });
  const water = w.scene.getObjectByName('front shallow water');
  const bed = w.scene.getObjectByName('continuous damp gravel channel');
  const pellets = w.scene.children.filter(o => o.name === 'submerged channel pebble');
  if (pellets.length !== 70) throw Error('Predecessor pellet inventory changed');
  const removedTriangles = pellets.reduce((n, o) => n + (o.geometry.index?.count ?? o.geometry.attributes.position.count) / 3, 0);
  pellets.forEach(o => { o.visible = false; });

  // The source uses t=(z-sourceZ)/sourceSpan, not a metre coordinate.
  // Reuse its physical longitudinal wavelengths, rather than fitting all 168
  // radians into this shorter 4.4 m enclosure. No wave amplitudes are changed.
  const p = water.geometry.attributes.position;
  const progress = Array.from({ length: p.count }, (_, i) => (p.getZ(i) - cfg.localStartZ) / cfg.sourceLongitudinalMetres);
  water.geometry.setAttribute('riverProgress', new T.Float32BufferAttribute(progress, 1));
  const adaptation = adaptWatercourseSlope(water.material, cfg);
  w.scene.updateMatrixWorld(true);

  const ray = new T.Raycaster(), down = new T.Vector3(0, -1, 0), vertex = new T.Vector3();
  const materials = new Map(), stoneRows = [];
  function hitAt(object, x, z) {
    ray.set(new T.Vector3(x, 5, z), down);
    const hit = ray.intersectObject(object, false)[0];
    if (!hit) throw Error(`Missing actual ${object.name} under stone at ${x}, ${z}`);
    return hit;
  }
  for (const placement of cfg.placements) {
    const src = assets.scanRocks?.scene.getObjectByName(placement.sourceName);
    if (!src?.isMesh || Array.isArray(src.material)) throw Error('Missing single-mesh scan');
    if (!materials.has(src.material)) {
      const material = src.material.clone();
      material.color.multiplyScalar(cfg.rockDiffuseLinearFactor);
      material.name = 'CC0 scanned channel stone, coherent rock diffuse factor';
      materials.set(src.material, material);
    }
    const stone = src.clone(true);
    stone.name = 'CC0 bed-seated stone ' + (stoneRows.length + 1);
    stone.material = materials.get(src.material);
    stone.position.set(0, 0, 0);
    stone.rotation.set(0, placement.yaw, 0);
    stone.scale.setScalar(1);
    stone.updateMatrixWorld(true);
    const originalBounds = new T.Box3().setFromObject(stone);
    const uniformScale = placement.height / originalBounds.getSize(new T.Vector3()).y;
    stone.scale.setScalar(uniformScale);
    stone.updateMatrixWorld(true);
    const center = new T.Box3().setFromObject(stone).getCenter(new T.Vector3());
    stone.position.set(placement.x - center.x, 0, placement.z - center.z);
    stone.updateMatrixWorld(true);
    const positions = stone.geometry.attributes.position, sampleRows = [];
    let lowestClearance = Infinity, lowestWitness;
    // Seat using every original scan vertex against the rendered bed triangles.
    // Nothing is placed against an analytic or invisible substitute plane.
    for (let i = 0; i < positions.count; i++) {
      vertex.fromBufferAttribute(positions, i).applyMatrix4(stone.matrixWorld);
      const bedHit = hitAt(bed, vertex.x, vertex.z);
      const waterHit = hitAt(water, vertex.x, vertex.z);
      const clearance = vertex.y - bedHit.point.y;
      if (clearance < lowestClearance) { lowestClearance = clearance; lowestWitness = { vertex: i, bedFace: bedHit.faceIndex, bedPoint: bedHit.point.toArray() }; }
      sampleRows.push({ y: vertex.y, bedY: bedHit.point.y, waterY: waterHit.point.y });
    }
    stone.position.y = -lowestClearance - cfg.stoneBurialMetres;
    stone.updateMatrixWorld(true);
    const signedBed = sampleRows.map(s => s.y + stone.position.y - s.bedY);
    const waterCover = sampleRows.map(s => s.waterY - (s.y + stone.position.y));
    const minimumCover = Math.min(...waterCover);
    if (minimumCover < cfg.minimumWaterCoverMetres) throw Error('Whole-mesh vertex cover below declared margin: ' + stone.name + ': ' + minimumCover);
    stone.castShadow = true;
    stone.receiveShadow = true;
    w.scene.add(stone);
    const box = new T.Box3().setFromObject(stone);
    const originalUVAndMaps = stone.geometry === src.geometry && stone.geometry.attributes.uv === src.geometry.attributes.uv
      && ['map', 'normalMap', 'roughnessMap', 'metalnessMap'].every(k => stone.material[k] === src.material[k]);
    if (!originalUVAndMaps) throw Error('Original scan UV/map identity lost');
    stoneRows.push({
      name: stone.name, sourceName: placement.sourceName, uniformScale, position: stone.position.toArray(), yaw: placement.yaw,
      triangles: stone.geometry.index.count / 3, sampledVertices: positions.count,
      supportMesh: bed.name, supportMethod: 'minimum signed clearance across all original scan vertices, raycast to actual bed triangles, then 8 mm burial',
      lowestWitness, minimumSignedBedClearanceMetres: Math.min(...signedBed), buriedVertexCount: signedBed.filter(x => x <= 0).length,
      minimumWaterCoverAcrossVerticesMetres: minimumCover, maximumWaterCoverAcrossVerticesMetres: Math.max(...waterCover),
      bounds: { min: box.min.toArray(), max: box.max.toArray() }, originalUVAndMaps,
    });
  }
  const addedTriangles = stoneRows.reduce((n, s) => n + s.triangles, 0);
  if (addedTriangles > 50000) throw Error('Bounded stone triangle budget exceeded');
  w.metadata.seatedWatercourseComparison = {
    scope: 'replace 70 pale pellets with eight CC0 scan stones; localize existing current cue to graded water; preserve channel, banks, optics, wind ripples, lights and cameras',
    source: 'https://polyhaven.com/a/rock_moss_set_01', license: 'CC0-1.0', rockDiffuseLinearFactor: cfg.rockDiffuseLinearFactor,
    preserved: ['water positions and indices', 'bed positions and indices', 'bank geometry/materials', 'world-space wind wave equations and amplitudes', 'all optical equations', 'time policy', 'cameras', 'lighting'],
    flow: {
      sourceSeed: cfg.sourceLandscapeSeed, sourceLongitudinalMetres: cfg.sourceLongitudinalMetres,
      localLongitudinalMetres: cfg.localLongitudinalMetres,
      previousPrimaryLongitudinalWavelengthMetres: 2 * Math.PI * cfg.localLongitudinalMetres / 168,
      sourceAndSuccessorPrimaryLongitudinalWavelengthMetres: 2 * Math.PI * cfg.sourceLongitudinalMetres / 168,
      previousSecondaryLongitudinalWavelengthMetres: 2 * Math.PI / (168 * .81 / cfg.localLongitudinalMetres + 11),
      sourceAndSuccessorSecondaryLongitudinalWavelengthMetres: 2 * Math.PI / (168 * .81 / cfg.sourceLongitudinalMetres + 11),
      progressRange: [Math.min(...progress), Math.max(...progress)],
      weighting: `smoothstep(${cfg.quietGrade}, ${cfg.fullGrade}, length(vNormal.xz)/abs(vNormal.y))`,
      interpretation: 'visual local-current approximation based on geometric grade, not measured or simulated hydraulics',
      changedFragmentEquationCount: adaptation.changedEquationCount,
    },
    stones: stoneRows,
    budget: {
      hiddenPellets: pellets.length, removedVisibleTriangles: removedTriangles, addedStoneCount: stoneRows.length,
      addedVisibleTriangles: addedTriangles, netVisibleTriangleDelta: addedTriangles - removedTriangles,
      addedVisibleMeshDraws: stoneRows.length, removedVisibleMeshDraws: pellets.length, netBasePassMeshDrawDelta: stoneRows.length - pellets.length,
      uniqueAddedGeometrySources: new Set(stoneRows.map(s => s.sourceName)).size,
      additionalTextureObjects: 0, additionalTextureDownloadBytes: 0, additionalTextureStorageEstimateBytes: 0,
      existingRockPackageBytes: 1933008, existingRockPackageTextureEstimateRGBA8WithMipsBytes: 16777216,
      note: 'shared original texture objects already loaded for foreground scan; render reports contain actual per-view triangle/call totals',
    },
    limits: ['Vertex-level bed/contact and cover witnesses do not certify every triangle interior', 'No screen-space refraction or indoor scene reflection', 'Existing bank/channel edge geometry is unchanged', 'Static two-view test only; no film, hydraulic simulation or animal support acceptance'],
  };
  const dispose = w.dispose;
  w.dispose = () => { for (const material of materials.values()) material.dispose(); dispose(); };
  return w;
}
